<template>
  <div class="step-input-data d-flex flex-column gap-3" data-testid="step-input-data__view">

    <!--  Upload Files Button  -->
    <base-button
      color="primary"
      label="Upload Data"
      prepend-icon="mdi-folder-open-outline"
      @click="openDrawerForUpload"
      data-testid="step-input-data__browse-btn"
      width="fit-content"
    />

    <!--  Uploaded Files Table  -->
    <div v-if="initialLoadDone && mappedFiles && mappedFiles.length > 0">
      <lb-datatableaction
        :headers="HEADERS"
        :tabledata="mappedFiles"
        :displayedRowCount="paginatedItems.length"
        :bulkActions="false"
        :showToolbar="true"
        :autoHeight="false"
        tableHeight="50vh"
        @filter-item="filterData"
        @clear-item="clearFilter"
        data-testid="step-input-data__main-table"
        class="step-input-data__main-table"
      >
        <template v-slot:tbody>
          <tr
            v-for="(row, idx) in paginatedItems"
            :key="row.id"
            class="cursor-pointer"
            :class="{ 'low-opacity': row.status === 'Deleted' }"
            data-testid="step-input-data__table-row"
          >
            <!-- S.No -->
            <td class="text-center px-3" data-testid="step-input-data__col-sno">
              <span class="body-2">{{ (currentPage - 1) * itemsPerPage + idx + 1 }}</span>
            </td>

            <!-- Uploaded By -->
            <td class="px-2" data-testid="step-input-data__col-uploader">
              <div class="step-input-data__uploader-cell">
                <div class="step-input-data__avatar">
                  <span>{{ row.uploaderInitial }}</span>
                </div>
                <div class="step-input-data__uploader-info">
                  <span class="step-input-data__uploader-name">{{ row.uploaderName }}</span>
                  <div class="step-input-data__uploader-email-chip">
                    <span>{{ row.uploaderEmail }}</span>
                  </div>
                </div>
              </div>
            </td>

            <!-- File Type -->
            <td class="px-2" data-testid="step-input-data__col-filetype">
              <span class="body-2 font-weight-semibold text-capitalize">{{ row.fileType }}</span>
            </td>

            <!-- File Name -->
            <td class="px-2" data-testid="step-input-data__col-filename">
              <span class="body-2" style="word-break: break-all;">{{ row.fileName }}</span>
            </td>

            <!-- Upload Type -->
            <td class="px-2" data-testid="step-input-data__col-uploadtype">
              <span class="body-2 text-capitalize">{{ row.uploadType }}</span>
            </td>

            <!-- Status -->
            <td class="px-2" data-testid="step-input-data__col-status">
              <status-indicator
                :variant="statusVariant(row.status)"
                :custom-label="statusLabel(row.status)"
                size="sm"
                data-testid="step-input-data__status-indicator"
              />
            </td>

            <!-- Updated Time -->
            <td class="px-2" data-testid="step-input-data__col-time">
              <div class="step-input-data__time-cell">
                <span>{{ row.time.split(',')[0] ? row.time.split(',')[0] + ',' : '' }}</span>
                <span>{{ row.time.split(',')[1] ? row.time.split(',')[1].trim() : '' }}</span>
              </div>
            </td>

            <!-- Action -->
            <td class="px-2" data-testid="step-input-data__col-action">
              <div class="d-flex align-center justify-center gap-1">
                <template v-if="row.status === 'Deleted'">
                  <v-tooltip left content-class="tooltip-left" max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" color="grey darken-1"
                        data-testid="step-input-data__deleted-info-btn">
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <div>
                      <div>
                        <strong>Deleted by:</strong>
                        {{ row.deletedBy ? (row.deletedBy.name || row.deletedBy.email || row.deletedBy) : 'N/A' }}
                      </div>
                      <div>
                        <strong>Deleted at:</strong>
                        {{ row.updatedAt ? $nova.formatDateTime(row.updatedAt) : 'N/A' }}
                      </div>
                    </div>
                  </v-tooltip>
                </template>
                <template v-else>
                  <v-tooltip bottom content-class="tooltip-bottom">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" color="primary"
                        @click.stop="$nova.downloadFile(row.id)"
                        data-testid="step-input-data__download-btn">
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>Download</span>
                  </v-tooltip>
                  <v-tooltip bottom content-class="tooltip-bottom">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" color="error"
                        @click.stop="promptDelete(row.id)"
                        data-testid="step-input-data__delete-btn">
                        <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="step-input-data__delete-icon"/>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </template>
              </div>
            </td>
          </tr>
        </template>
      </lb-datatableaction>
    </div>

    <!--  Delete Confirmation Dialog  -->
    <confirm-delete-dialog
      v-if="showDeleteDialog"
      v-model="showDeleteDialog"
      message="Are you sure you want to delete this file?"
      sub-message="This action will mark the file as <strong>Deleted</strong> and cannot be undone."
      data-testid="step-input-data"
      @confirm="confirmDelete"
    />


    <!--  Upload Type Dialog  -->
    <upload-data-dialog
      v-if="typeOffile"
      v-model="typeOffile"
      :loading="typeOffileloading"
      :fileuploadeCount="fileuploadeCount"
      :files="files"
      :twofileId.sync="twofileId"
      :uploadTypeItems="currentUploadTypeItems"
      :fileuptype.sync="fileuptype"
      :fileuptypefile="fileuptypefile"
      :fileuploadeList="fileuploadeList"
      :fileTableHeaders="headersFile"
      :errorMessage="singleMultfileError"
      :hasFileErrors="hasFileErrors"
      :isDisabled="isUploadDialogDisabled"
      @file-type-change="onFileTypeChange"
      @file-input-change="onFileInputChange"
      @delete-file="deleteFile"
      @cancel="closeUploadDialog"
      @confirm="confirmUpload"
    />
    <Pagination
      :list="filteredFiles"
      :itemsperpage="itemsPerPage"
      @paginated-data="setPaginatedData"
      data-testid="step-input-data__pagination"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import StatusIndicator from '@/components/StatusIndicator.vue';
import { FILE_STAGES, UPLOAD_TYPES, UPLOAD_TYPE_OPTIONS_NEW, UPLOAD_TYPE_OPTIONS_EXISTING, TM_RECONCILIATION } from '@/modules/financialclose/utils/constants.js';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
import UploadDataDialog from './UploadDataDialog.vue';
import Pagination from '../../../../components/Pagination/Pagination.vue';

export default {
  name: 'StepInputDataV2',

  components: {
    StatusIndicator,
    ConfirmDeleteDialog,
    UploadDataDialog,
    Pagination,
  },

  props: {
    files: {
      type: Array,
      required: true,
    },
    filevalues: {
      type: Object,
      required: true,
    },
    fileproerr: {
      type: Object,
      required: true,
    },
    fileprostage: {
      type: Object,
      required: true,
    },
    fileuppercent: {
      type: Object,
      required: true,
    },
    storepath: {
      type: String,
      required: true,
    },
    fileuploadeCount: {
      type: String,
      required: true,
    },
    botId: {
      type: String,
      required: true,
    },
    filesdataPros: {
      type: Object,
      required: true,
    },
    fileconfig: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showDeleteDialog: false,
      deleteWarningId: null,
      filecount: 0,
      fileuploadpercent: {},
      file: {},
      typeOffile: false,
      typeOffileloading: false,
      fileuploadtypeItem: UPLOAD_TYPE_OPTIONS_EXISTING,
      fileuploadtypeItemNofile: UPLOAD_TYPE_OPTIONS_NEW,
      fileuptypefile: null,
      fileuptype: '',
      singleMultfileError: '',
      twofileId: '',
      twofileColum: '',
      fileUpDetil: '',
      requiredColum: null,
      getstoreFileid: null,
      fileuploadeList: [],
      headersFile: [
        { text: 'File Name', value: 'name',   visible: true, sortable: false, filterable: false },
        { text: 'Status',    value: 'status', visible: true, sortable: false, filterable: false, align: 'center' },
        { text: 'Action',    value: 'action', visible: true, sortable: false, filterable: false, align: 'center' },
      ],
      firstKey: '',
      fileuploadeornot: false,
      uploadedFilesData: null,
      initialLoadDone: false,
      tableFilters: {},
      filterActive: false,
      fileStatusPollingInterval: null,
      itemsPerPage: 15,
      paginatedItems: [],
      currentPage: 1,
    };
  },

  computed: {
    ...mapGetters('transactionMatching', {
      analysisDetails: 'analysisDetails',
    }),

    isFileUploadDisabled() {
      const sourceData = this.uploadedFilesData || this.filevalues;
      if (!sourceData || !Array.isArray(sourceData.files)) return false;
      return sourceData.files.some(
        f => f.stage === FILE_STAGES.QUEUED || f.stage === FILE_STAGES.PROCESSING || f.processed === FILE_STAGES.QUEUED || f.processed === FILE_STAGES.PROCESSING
      );
    },

    currentUploadTypeItems() {
      if (this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE || this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.THREE_FILE) {
        const hasFiles = this._hasExistingFilesForType(this.twofileId);
        return hasFiles ? UPLOAD_TYPE_OPTIONS_EXISTING : UPLOAD_TYPE_OPTIONS_NEW;
      }
      const hasAnyFiles = this._hasAnyExistingFiles();
      return hasAnyFiles ? UPLOAD_TYPE_OPTIONS_EXISTING : UPLOAD_TYPE_OPTIONS_NEW;
    },

    HEADERS() {
      return [
        { text: 'S.No',             value: 'sno',          visible: true, sortable: false, filterable: false },
        { text: 'Uploaded By',      value: 'uploaderName', visible: true, sortable: false, filterable: true },
        { text: 'Data Type',        value: 'fileType',     visible: true, sortable: false, filterable: true },
        { text: 'File Name',        value: 'fileName',     visible: true, sortable: false, filterable: true },
        { text: 'Upload Type',      value: 'uploadType',   visible: true, sortable: false, filterable: true },
        { text: 'Ingestion Status', value: 'status',       visible: true, sortable: false, filterable: true },
        { text: 'Uploaded Time',     value: 'time',         visible: true, sortable: false, filterable: true },
        { text: 'Action',           value: 'action',       visible: true, sortable: false, filterable: false, align: 'center' },
      ];
    },

    filteredFiles() {
      const filters = this.tableFilters;
      if (!filters || Object.keys(filters).length === 0) return this.mappedFiles;
      return this.mappedFiles.filter(row =>
        Object.entries(filters).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          const cellValue = String(row[key] !== undefined && row[key] !== null ? row[key] : '');
          return values.some(v => String(v) === cellValue);
        })
      );
    },

    mappedFiles() {
      const list = [];
      const sourceData = this.uploadedFilesData;

      const processItem = (item, fileDefKey) => {
        let fileDef = this.files?.find(
          f => f._id === fileDefKey || f._id === item.fileid || f._id === item.fileId
        ) || {};
        if (!fileDef._id && item.fileType) {
          const fallback = this.files?.find(
            f => f.name && f.name.toLowerCase() === item.fileType.toLowerCase()
          );
          if (fallback) fileDef = fallback;
        }

        const fileTypeStr = (fileDef.name || item.fileType || 'Dataset').replace(/_/g, ' ');

        let userName  = 'User';
        let userEmail = 'user@consark.ai';
        if (item.uploadedBy?.name) {
          userName  = item.uploadedBy.name;
          userEmail = item.uploadedBy.email || userEmail;
        } else {
          userName  = this.$store?.state?.user?.firstname || this.$store?.state?.user?.name || 'User';
          userEmail = this.$store?.state?.user?.email || userEmail;
        }

        // Use epoch 0 for missing createdAt — avoids timestamp changing on every poll
        const dt = item.createdAt ? new Date(item.createdAt) : new Date(0);
        const timeStr =
          dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
          ', ' +
          dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

        let statusStr = 'Initiated';
        if      (item.stage === FILE_STAGES.SUCCESSFUL   || item.processed === FILE_STAGES.SUCCESSFUL)   statusStr = 'Success';
        else if (item.stage === FILE_STAGES.QUEUED       || item.processed === FILE_STAGES.QUEUED)       statusStr = 'Initiated';
        else if (item.stage === FILE_STAGES.PROCESSING   || item.processed === FILE_STAGES.PROCESSING)   statusStr = 'InProgress';
        else if (item.stage === FILE_STAGES.FAILED       || item.processed === FILE_STAGES.FAILED)       statusStr = 'Failed';
        else if (item.stage === FILE_STAGES.DELETED      || item.processed === FILE_STAGES.DELETED)      statusStr = 'Deleted';

        list.push({
          id:              item.uploadId || item._id,
          uploaderInitial: userName.charAt(0).toUpperCase(),
          uploaderName:    userName,
          uploaderEmail:   userEmail,
          fileType:        fileTypeStr,
          fileName:        item.filename,
          uploadType:      (item.uploadType || item.uploadtype) === 'Delete File' ? '' : (item.uploadType || item.uploadtype || 'Replace/Add'), // TODO - this is a hack. we need to fix this properly
          status:          statusStr,
          time:            timeStr,
          ts:              dt.getTime(),
          uploadedAt:      item.uploadedAt || item.createdAt || null,
          updatedAt:       item.updatedAt || null,
          uploadedBy:      item.uploadedBy || null,
          deletedBy:       item.deletedBy || null,
          originalItem:    item,
        });
      };

      if (sourceData) {
        if (Array.isArray(sourceData)) {
          sourceData.forEach(item => processItem(item, item.fileid || item.fileId || item.file));
        } else if (Array.isArray(sourceData.files)) {
          sourceData.files.forEach(item => processItem(item, item.fileid || item.fileId || item.file));
        } else if (typeof sourceData === 'object' && !sourceData.files) {
          for (const key in sourceData) {
            if (Array.isArray(sourceData[key])) {
              sourceData[key].forEach(item => processItem(item, key));
            }
          }
        }
      }

      // Stable sort: descending by timestamp, then by id as tiebreaker to prevent row shuffling
      return list.sort((a, b) => {
        if (b.ts !== a.ts) return b.ts - a.ts;
        return String(a.id).localeCompare(String(b.id));
      });
    },

    isAllFileProcessed() {
      return id => this._allfileprocessed(this.filevalues[id]);
    },

    hasFileErrors() {
      return this.fileuploadeList.some(f => f._validating || f._valid === false);
    },

    isUploadDialogDisabled() {
      const sourceData = this.uploadedFilesData;
      if (!sourceData) return false;

      const targetId = this.twofileId || this.getstoreFileid;
      let filesToCheck = [];

      if (Array.isArray(sourceData)) {
        filesToCheck = targetId
          ? sourceData.filter(f => f.fileid === targetId || f.fileId === targetId || f.file === targetId)
          : sourceData;
      } else if (Array.isArray(sourceData.files)) {
        filesToCheck = targetId
          ? sourceData.files.filter(f => f.fileid === targetId || f.fileId === targetId || f.file === targetId)
          : sourceData.files;
      } else if (typeof sourceData === 'object') {
        if (targetId && Array.isArray(sourceData[targetId])) {
          filesToCheck = sourceData[targetId];
        } else {
          for (const key in sourceData) {
            if (Array.isArray(sourceData[key])) filesToCheck = [...filesToCheck, ...sourceData[key]];
          }
        }
      }

      return filesToCheck.some(
        f => f.stage === FILE_STAGES.QUEUED || f.stage === FILE_STAGES.PROCESSING ||
             f.processed === FILE_STAGES.QUEUED || f.processed === FILE_STAGES.PROCESSING
      );
    },
  },

  mounted() {
    this.getstoreFileid = Object.keys(this.fileconfig)[0];
    this.firstKey       = Object.keys(this.fileconfig)[0];
    this.requiredColum  = this.fileconfig[this.firstKey];
    this.getFileUploadStatus();
    this.startFileStatusPolling();
  },

  beforeDestroy() {
    this.fileuploadeList   = [];
    this.uploadedFilesData = null;
    this.fileuptype        = '';
    this.twofileId         = '';
    this.tableFilters      = {};
    this.filterActive      = false;
    this.initialLoadDone   = false;
    this.stopFileStatusPolling();
  },

  methods: {
    //  Polling 
    startFileStatusPolling() {
      this.stopFileStatusPolling();
      this.fileStatusPollingInterval = setInterval(() => {
        this.getFileUploadStatus();
      }, 30000);
    },

    stopFileStatusPolling() {
      if (this.fileStatusPollingInterval) {
        clearInterval(this.fileStatusPollingInterval);
        this.fileStatusPollingInterval = null;
      }
    },

    //  Filter handlers 
    filterData(filters) {
      this.tableFilters = filters || {};
    },

    clearFilter() {
      this.tableFilters = {};
    },

    setPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedItems = paginationInfo.data || [];
        this.currentPage = paginationInfo.page || 1;
      } else {
        this.paginatedItems = paginationInfo || [];
        this.currentPage = 1;
      }
    },

    onFilterActiveChange(isActive) {
      this.filterActive = isActive;
    },

    //  Status helpers 
    statusLabel(status) {
      const map = {
        Success: 'Success',
        Failed:     'Failed',
        Error:      'Failed',
        Deleted:    'Deleted',
        InProgress: 'In Progress',
        Processing: 'In Progress',
      };
      return map[status] || status || 'Initiated';
    },

    statusVariant(status) {
      const map = {
        Success: 'approved',
        Failed:     'failed',
        Error:      'failed',
        Deleted:    'inactive',
        InProgress: 'in-progress',
        Processing: 'in-progress',
        Initiated:  'pending-approval',
      };
      return map[status] || 'unknown-status';
    },

    //  Upload dialog 
    openDrawerForUpload() {
      // if (this.isFileUploadDisabled) return;
      if (this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.MORE || this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.ONE_FILE) {
        this._openUploadDialog();
      } else if (this.files?.length > 0) {
        const first = this.files[0];
        this._openUploadDialogTwoFile(first._id, first.columns, this.filevalues[first._id]);
      }
    },

    _openUploadDialog() {
      this.fileuptypefile      = null;
      this.fileuptype          = '';
      this.singleMultfileError = '';
      this.fileuploadeList     = [];
      this.typeOffile          = true;
    },

    _openUploadDialogTwoFile(id, column, fileexit) {
      this.fileuploadeornot    = !fileexit;
      this.fileuptypefile      = null;
      this.fileuptype          = '';
      this.twofileId           = id;
      this.twofileColum        = column;
      this.singleMultfileError = '';
      this.fileuploadeList     = [];
      this.typeOffile          = true;
    },

    closeUploadDialog() {
      this.typeOffile          = false;
      this.fileuploadeList     = [];
      this.singleMultfileError = '';
      this.fileuptype          = '';
    },

    onFileTypeChange() {
      const selected = this.files?.find(f => f._id === this.twofileId);
      if (selected) {
        this.twofileColum     = selected.columns;
        this.fileuploadeornot = !this.filevalues[selected._id];
        this.fileuptype       = '';
      }
      // Clear any pending file input so a fresh pick is possible after source type change
      this.fileuptypefile = null;
      this.fileuploadeList = [];
      // Re-validate all already-selected files against the new data source type's columns
      if (this.fileuploadeList.length > 0) {
        this.fileuploadeList.forEach(wrapper => {
          this.$set(wrapper, '_validating', true);
          this.$set(wrapper, '_valid', null);
          this.$set(wrapper, '_error', null);
          this._validateAndUpdateWrapper(wrapper);
        });
      }
    },

    onFileInputChange(newFiles) {
      if (!newFiles) return;
      const files = Array.isArray(newFiles) ? newFiles : [newFiles];
      this.singleMultfileError = '';
      for (const f of files) {
        const sanitizedName = this._sanitizeFileName(f.name);
        const rawFile = sanitizedName !== f.name
          ? new File([f], sanitizedName, { type: f.type, lastModified: f.lastModified })
          : f;
        const wrapper = { name: sanitizedName, _raw: rawFile, _validating: true, _valid: null, _error: null };
        this.fileuploadeList.push(wrapper);
        this._validateAndUpdateWrapper(wrapper);
      }
      // Reset so lb-file can accept the same files again
      this.$nextTick(() => { this.fileuptypefile = null; });
    },

    confirmUpload() {
      if (this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE || this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.THREE_FILE) {
        this.uploadeFileTwo(this.fileuptype, this.fileuploadeList);
      } else {
        this.uploadeFiles(this.fileuptype, this.fileuploadeList, this.fileuploadeCount);
      }
    },

    //  Delete dialog 
    promptDelete(id) {
      this.deleteWarningId  = id;
      this.showDeleteDialog = true;
    },

    confirmDelete() {
      if (this.deleteWarningId !== null) {
        this.uploadFileDelete(this.deleteWarningId);
        this.deleteWarningId  = null;
        this.showDeleteDialog = false;
      }
    },

    //  API calls 
    async getFileUploadStatus() {
      try {
        const response = await this.axios.post('/v2/bot/analysis/fileuploadstatus/' + this.$route.params.anaid);
        if (response?.data?.status === 'success') {
          const newData = response.data.data;
          // Only update if data actually changed — prevents mappedFiles recompute on every poll
          if (JSON.stringify(newData) !== JSON.stringify(this.uploadedFilesData)) {
            this.uploadedFilesData = newData;
          }
        }
      } catch (err) {
        console.error('[StepInputDataV2]: getFileUploadStatus error', err);
      } finally {
        this.initialLoadDone = true;
      }
    },

    async uploadeFileTwo(type, selectfiles) {
      if (!type)                               { this.singleMultfileError = 'Upload Type Required!'; return; }
      if (!selectfiles || !selectfiles.length) { this.singleMultfileError = 'File Required!';        return; }

      this.typeOffileloading = true;
      const fileIds = [];
      let allUploaded = true;

      for (const fileItem of selectfiles) {
        try {
          const rawFile = fileItem._raw || fileItem;
          const uploadResponse = await this.$nova.uploadFile(this.axios, rawFile, {
            progressfunction: (e) => {
              this.fileuploadpercent[this.twofileId] = ((e.loaded * 100) / e.total).toFixed(0);
              this.filecount++;
            },
          });
          if (uploadResponse.data.status !== 'success') throw new Error('Unable to upload file');
          fileIds.push(uploadResponse.data.data[0]._id);
        } catch (error) {
          console.error('[StepInputDataV2]: uploadeFileTwo upload error', error);
          this.singleMultfileError = error.message || 'Unknown error!';
          this.$store.commit('sbError', error.message || 'Upload failed', { root: true });
          allUploaded = false;
          break;
        }
      }

      if (!allUploaded || !fileIds.length) { this.typeOffileloading = false; return; }

      try {
        const fileTypeKeys = selectfiles.map(f => f._fileTypeKey || null);
        const storeResponse = await this.axios.post(this.storepath, {
          data: { upload: fileIds, file: this.twofileId, uploadtype: type, tmaid: this.analysisDetails._id, filetype: fileTypeKeys },
        });

        if (storeResponse.data.status !== 'success')
          throw new Error(storeResponse.data.message || 'Error storing file');

        this.typeOffile          = false;
        this.fileUploadedDetails = storeResponse.data.data;
        this.$store.commit('sbSuccess', 'All files uploaded successfully', { root: true });
        this._refreshAfterUpload(null);

      } catch (error) {
        console.error('[StepInputDataV2]: uploadeFileTwo store error', error);
        this.singleMultfileError = error.message || 'Unknown error!';
        this.$store.commit('sbError', error.message || 'Upload failed', { root: true });
      } finally {
        this.typeOffileloading = false;
      }
    },

    async uploadeFiles(type, selectfiles, filecount) {
      if (!type)                               { this.singleMultfileError = 'Upload Type Required!'; return; }
      if (!selectfiles || !selectfiles.length) { this.singleMultfileError = 'File Required!';        return; }
      if (filecount !== TM_RECONCILIATION.FILE_TYPES.MORE && filecount !== TM_RECONCILIATION.FILE_TYPES.ONE_FILE) return;

      this.typeOffileloading = true;
      const fileIds  = [];
      let allUploaded = true;

      for (const fileItem of selectfiles) {
        try {
          const rawFile = fileItem._raw || fileItem;
          const uploadResponse = await this.$nova.uploadFile(this.axios, rawFile, {
            progressfunction: (e) => {
              this.fileuploadpercent = ((e.loaded * 100) / e.total).toFixed(0);
            },
          });
          if (uploadResponse.data.status !== 'success') throw new Error('Unable to upload file');
          fileIds.push(uploadResponse.data.data[0]._id);
        } catch (error) {
          this.singleMultfileError = error.message || 'Unknown error!';
          console.error('[StepInputDataV2]: uploadeFiles upload error', error);
          allUploaded = false;
          break;
        }
      }

      if (!allUploaded || !fileIds.length) { this.typeOffileloading = false; return; }

      try {
        const fileTypeKeys = selectfiles.map(f => f._fileTypeKey || null);
        const storeResponse = await this.axios.post(this.storepath, {
          data: { upload: fileIds, file: this.getstoreFileid, uploadtype: type, tmaid: this.analysisDetails._id, isittm: true, filetype: fileTypeKeys },
        });
        if (storeResponse.data.status !== 'success')
          throw new Error(storeResponse.data.message || 'Error sending file IDs');
        this.$store.commit('sbSuccess', 'All files uploaded successfully', { root: true });
        this._refreshAfterUpload(null);
      } catch (error) {
        this.$store.commit('sbError', error.message || 'Upload failed', { root: true });
        console.error('[StepInputDataV2]: uploadeFiles store error', error);
      } finally {
        this.file              = {};
        this.typeOffileloading = false;
        this.typeOffile        = false;
      }
    },

    _refreshAfterUpload(file) {
      this.getFileUploadStatus();
      this.$emit('storedData', { stored: this.fileUploadedDetails, uploaded: file });
      [10000, 20000].forEach(delay =>
        setTimeout(() => {
          this.getFileUploadStatus();
          this.$emit('storedData', { stored: this.fileUploadedDetails, uploaded: file });
        }, delay)
      );
    },

    uploadFileDelete(id) {
      this.$emit('startloading');
      this.axios
        .post('/v2/bot/analysis/deletefile/' + this.botId + '/' + id, { uploadtype: UPLOAD_TYPES.DELETE_FILE })
        .then(async res => {
          if (res.data.status === 'success') {
            this.$emit('storedData', { stored: this.fileUploadedDetails });
            // Await the status refresh so uploadedFilesData is up-to-date before
            // stoploading fires — prevents isUploadDialogDisabled from using stale
            // QUEUED/PROCESSING state and blocking the next upload attempt.
            await this.getFileUploadStatus();
          } else {
            throw new Error(res.data.message || 'Error deleting file');
          }
        })
        .catch(err => {
          this.$store.commit('sbError', err.message || 'Unknown error!', { root: true });
          console.error('[StepInputDataV2]: uploadFileDelete error', err);
        })
        .finally(() => this.$emit('stoploading'));
    },

    deleteFile(index) {
      this.fileuploadeList.splice(index, 1);
    },

    callOtherFunction() {
      this.$emit('storedData', {});
      setTimeout(() => this.$emit('storedData', {}), 20000);
    },

    _allfileprocessed(arr) {
      return Array.isArray(arr) && arr.every(item => item.processed === 1);
    },

    _hasExistingFilesForType(fileTypeId) {
      const sourceData = this.uploadedFilesData || this.filevalues;
      if (!sourceData) return false;
      if (Array.isArray(sourceData.files)) {
        return sourceData.files.some(
          f => (f.fileid === fileTypeId || f.fileId === fileTypeId) && f.stage !== FILE_STAGES.DELETED && f.processed !== FILE_STAGES.DELETED
        );
      }
      if (sourceData[fileTypeId]) {
        const arr = sourceData[fileTypeId];
        return Array.isArray(arr) && arr.some(f => f.stage !== FILE_STAGES.DELETED && f.processed !== FILE_STAGES.DELETED);
      }
      return false;
    },

    _hasAnyExistingFiles() {
      const sourceData = this.uploadedFilesData || this.filevalues;
      if (!sourceData) return false;
      if (Array.isArray(sourceData.files)) {
        return sourceData.files.some(f => f.stage !== FILE_STAGES.DELETED && f.processed !== FILE_STAGES.DELETED);
      }
      if (typeof sourceData === 'object') {
        return Object.values(sourceData).some(
          arr => Array.isArray(arr) && arr.some(f => f.stage !== FILE_STAGES.DELETED && f.processed !== FILE_STAGES.DELETED)
        );
      }
      return false;
    },

    // Replace characters disallowed by both $nova.FILEREGEX (Excel) and readCSV's local regex with underscores.
    // Note: @ is intentionally excluded — readCSV rejects filenames containing @.
    _sanitizeFileName(name) {
      return name.replace(/[^a-zA-Z0-9 _.()-]/g, '_');
    },

    //  File header validation 
    async _validateAndUpdateWrapper(wrapper) {
      try {
        const isTwoThree = this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE || this.fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.THREE_FILE;
        let configKey;
        if (isTwoThree) {
          configKey = this.twofileId;
        } else {
          configKey = this.getstoreFileid || Object.keys(this.fileconfig)[0];
        }
        const config = this.fileconfig[configKey] || {};
        const result = await this._checkFileHeaders(wrapper._raw, config);
        this.$set(wrapper, '_validating', false);
        this.$set(wrapper, '_valid',      result.valid);
        this.$set(wrapper, '_error',      result.error);
        this.$set(wrapper, '_fileTypeKey', result.fileTypeKey || null);
      } catch (e) {
        this.$set(wrapper, '_validating', false);
        this.$set(wrapper, '_valid',      false);
        this.$set(wrapper, '_error',      e.message || 'Validation failed');
      }
    },

    async _checkFileHeaders(file, config) {

      if (!config.fileTypes || config.fileTypes.length === 0) {
        console.log('[_checkFileHeaders]: No fileTypes defined in config — skipping validation, returning valid=true');
        return { valid: true, error: null, fileTypeKey: null };
      }

      console.log('[_checkFileHeaders]: fileTypes to validate against:', config.fileTypes);

      try {
        const ext = (file.name || '').split('.').pop().toLowerCase();
        console.log('[_checkFileHeaders]: Detected file extension:', ext);

        // Pass ignoretop/ignoreleft so readExcelFile reads ALL raw rows (no skipping)
        // We apply ignorerows/ignorecolumns manually below
        const rawConfig = { ignoretop: 0, ignoreleft: 0 };
        let uploadData = [];
        if (['xlsx', 'xls', 'xlsm', 'xlsb'].includes(ext)) {
          console.log('[_checkFileHeaders]: Reading as Excel file with rawConfig:', rawConfig);
          // Pass blank=true (4th arg) so xlsx keeps empty rows/columns —
          // preserving original row and column indices. Without this, blank
          // rows/columns before the used range are stripped, making
          // uploadData[ignorerows] / slice(ignorecolumns) point to wrong data.
          uploadData = await this.$nova.readExcelFile(file, '', rawConfig, true);
          console.log('[_checkFileHeaders]: Excel read complete — total rows:', uploadData?.length);
        } else if (ext === 'csv') {
          console.log('[_checkFileHeaders]: Reading as CSV file');
          uploadData = await this.$nova.readCSV(file, { top: 0, left: 0 });
          console.log('[_checkFileHeaders]: CSV read complete — total rows:', uploadData?.length);
        } else {
          console.warn('[_checkFileHeaders]: Unsupported file extension:', ext);
          return { valid: false, error: 'Unsupported file type (.xlsx/.xls/.xlsm/.xlsb/.csv only)', fileTypeKey: null };
        }

        if (!uploadData?.length) {
          console.warn('[_checkFileHeaders]: uploadData is empty or null after reading');
          return { valid: false, error: 'File is empty or cannot be read', fileTypeKey: null };
        }

        // Each fileType carries its own ignorerows/ignorecolumns/blankcolumnheaders —
        // read them from ft, not from the top-level config.
        for (const ft of config.fileTypes) {
          console.log(`[_checkFileHeaders]: ── Checking fileType "${ft.key}" ──`);

          const ignorerows         = Number(ft.ignorerows) || 0;
          const ignorecolumns      = Number(ft.ignorecolumns) || 0;
          const blankcolumnheaders = ft.blankcolumnheaders || 'left';
          // ignorerows = N means skip the first N rows; header row is at index N (0-based)
          const headerRowIndex = ignorerows;

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — ignorerows:`, ignorerows, '| ignorecolumns:', ignorecolumns, '| blankcolumnheaders:', blankcolumnheaders, '| headerRowIndex:', headerRowIndex);

          const selectRow = [...(uploadData[headerRowIndex] || [])];
          let copyindex = 0;
          if (blankcolumnheaders === 'bottom') copyindex = 1;
          else if (blankcolumnheaders === 'top') copyindex = -1;

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — copyindex:`, copyindex, '| blankRow index:', headerRowIndex + copyindex);

          const blankRow    = [...(uploadData[headerRowIndex + copyindex] || [])];
          const slicedRow   = selectRow.slice(ignorecolumns);
          const slicedBlank = blankRow.slice(ignorecolumns);

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — selectRow:`, selectRow);
          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — blankRow:`, blankRow);
          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — slicedRow:`, slicedRow);
          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — slicedBlank:`, slicedBlank);

          const finalHeaders = [];
          for (let i = 0; i < slicedRow.length; i++) {
            let h = String(slicedRow[i] || '').trim();
            const b = String(slicedBlank[i] || '').trim();
            if (h === '' && b !== '') {
              console.log(`[_checkFileHeaders]: fileType "${ft.key}" col[${i}] — header empty, using blankRow value: "${b}"`);
              h = b;
            } else if (h === '' && b === '' && blankcolumnheaders === 'left' && i > 0) {
              const prev = String(slicedRow[i - 1] || '').trim();
              console.log(`[_checkFileHeaders]: fileType "${ft.key}" col[${i}] — header and blankRow both empty, copying left neighbour: "${prev}"`);
              h = prev;
            }
            finalHeaders.push(h);
          }

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — finalHeaders (pre-sort, pre-filter):`, finalHeaders);

          const fileHeadersSorted = finalHeaders.map(h => h.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b));

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — fileHeadersSorted:`, fileHeadersSorted);

          const expected = (config.columnmap || [])
            .filter(cm => cm.fileTypeAliases?.[ft.key]?.header)
            .map(cm => String(cm.fileTypeAliases[ft.key].header).trim())
            .sort((a, b) => a.localeCompare(b));

          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — expected headers from columnmap:`, expected);

          if (!expected.length) {
            console.log(`[_checkFileHeaders]: fileType "${ft.key}" — no expected headers in columnmap, skipping`);
            continue;
          }

          const missingHeaders = expected.filter(hdr => !fileHeadersSorted.includes(hdr));
          console.log(`[_checkFileHeaders]: fileType "${ft.key}" — missing:`, missingHeaders);

          if (missingHeaders.length === 0) {
            console.log(`[_checkFileHeaders]: MATCH found for fileType "${ft.key}" — returning valid=true`);
            return { valid: true, error: null, fileTypeKey: ft.key };
          }
        }

        console.warn('[_checkFileHeaders]: No matching fileType found — returning valid=false (Missing required columns)');
        return { valid: false, error: 'Missing required columns', fileTypeKey: null };
      } catch (e) {
        console.error('[_checkFileHeaders]: Exception caught:', e);
        return { valid: false, error: e.message || 'Could not read file headers', fileTypeKey: null };
      }
    },
  },

  watch: {
    filevalues(newVal) {
      void newVal;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './StepInputDataV2.scss';
</style>
